import { z } from 'zod';
import { paymentModeSchema } from './account';
import { orderStatusSchema } from './order';
import { productSchema, SCOPES, SCRIPT_LENGTHS } from './products';
import { projectStatusSchema } from './project';
import { scriptAnalysisSchema } from './script-analysis';
import { scriptProjectSchema } from './script-project';
import { scriptVersionStatusSchema } from './script-version';
import { titleProjectSchema } from './title-project';

const timestamp = z.iso.datetime({ offset: true });

/**
 * What a queue row asks staff to do: a whole project, or a customer's later version of its script. Other kinds of
 * work (an additional clearance, a question) are reserved: they would be unioned into the same list.
 */
export const WORK_QUEUE_TASK_KINDS = ['project', 'version'] as const;

/**
 * A row of `staff_list_work_queue` (`staff.projects.view`): a project whose main order is placed and which is not
 * yet delivered, followed by a `version` row for each of its script's versions from 2 on that is not confirmed.
 * Soonest due first. No amount: fulfilment does not need one.
 */
const workQueueRowObject = z.object({
  task_kind: z.enum(WORK_QUEUE_TASK_KINDS),
  project_id: z.uuid(),
  product: productSchema,
  title: z.string(),
  organization_id: z.uuid(),
  organization_name: z.string(),
  workspace_name: z.string(),
  turnaround_days: z.number().int(),
  scope: z.enum(SCOPES),
  script_length: z.enum(SCRIPT_LENGTHS).nullable(),
  ordered_at: timestamp,
  due_at: timestamp,
  status: projectStatusSchema,
  /** A project row: version 1's status (null for a title). A version row: that version's. */
  version_status: scriptVersionStatusSchema.nullable(),
  /** The version a `version` row is about; all four are null on a project row. The label is shown, never logged. */
  script_version_id: z.uuid().nullable(),
  version_number: z.number().int().min(2).nullable(),
  label: z.string().nullable(),
  version_created_at: timestamp.nullable(),
});
export const workQueueRowSchema = workQueueRowObject.refine(
  (row) =>
    row.task_kind === 'version'
      ? row.script_version_id !== null &&
        row.version_number !== null &&
        row.version_created_at !== null &&
        row.version_status !== null
      : row.script_version_id === null && row.version_number === null && row.label === null,
  { message: 'a version row names its version; a project row names none' },
);
export type WorkQueueRow = z.infer<typeof workQueueRowSchema>;

/**
 * What `staff_get_project_intake` answers (`staff.projects.view`): everything the customer told us about a
 * project. Names are profile names, never an email; there is no amount and no storage path.
 */
export const staffProjectIntakeSchema = z.object({
  project: z.object({
    id: z.uuid(),
    name: z.string(),
    product: productSchema,
    status: projectStatusSchema,
    ordered_at: timestamp.nullable(),
    due_at: timestamp.nullable(),
    workspace_id: z.uuid(),
    workspace_name: z.string(),
    organization_id: z.uuid(),
    organization_name: z.string(),
  }),
  /** The row of `title_projects` or `script_projects`. */
  details: z.union([
    scriptProjectSchema.omit({ project_id: true }),
    titleProjectSchema.omit({ project_id: true }),
  ]),
  /** The live main order, or the last one when it was cancelled or refunded. */
  order: z
    .object({
      id: z.uuid(),
      status: orderStatusSchema,
      options: z.record(z.string(), z.unknown()),
      reference: z.string().nullable(),
      payment_mode: paymentModeSchema,
      submitted_at: timestamp.nullable(),
      placed_at: timestamp.nullable(),
      customer_time_zone: z.string().nullable(),
      ordered_by_name: z.string().nullable(),
      paid_by_name: z.string().nullable(),
    })
    .nullable(),
  entitlements: z
    .object({ additional_clearances: z.number().int(), versions: z.number().int() })
    .nullable(),
  script: z
    .object({
      file_id: z.uuid(),
      original_filename: z.string(),
      size_bytes: z.number().int(),
      uploaded_at: timestamp,
    })
    .nullable(),
  /** Every version of the script in order (null for a title). The label is shown, never logged. */
  versions: z
    .array(
      z.object({
        id: z.uuid(),
        number: z.number().int().positive(),
        label: z.string().nullable(),
        status: scriptVersionStatusSchema,
        created_at: timestamp,
        confirmed_at: timestamp.nullable(),
      }),
    )
    .nullable(),
  /** Versions after the first, which count against `entitlements.versions` (null for a title). */
  versions_used: z.number().int().nonnegative().nullable(),
  analysis: scriptAnalysisSchema
    .pick({
      status: true,
      word_count: true,
      page_count: true,
      length: true,
      extraction_status: true,
      extraction: true,
      completed_at: true,
    })
    .nullable(),
});
export type StaffProjectIntake = z.infer<typeof staffProjectIntakeSchema>;
